import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Loader from "../../loader/Loader";
import { domain } from "../../../security";
import { selectUserID } from "../../../redux/IchthusSlice";
import { RefreshCw, ImagePlus } from "lucide-react";
import licensePlaceholder from "../../../Images/profile.jpg";

// ─── exact same helper as AddEmployee ────────────────────────────────────────
const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const RESTRICTION_CODES = [
  "A", // Motorcycle
  "A1", // Motorcycle (variant)
  "B", // Cars up to 3,500 kg
  "B1", // Variant
  "B2", // Variant
  "C", // 3,500–12,000 kg vehicles
  "D", // Above 12,000 kg
  "BE", // Light vehicles with trailer
  "CE", // Medium vehicles with trailer
  "DE", // Heavy vehicles with trailer
  "E", // Special equipment (forklift, etc.)
];

// Premium modern input styles
const inputCls =
  "mt-1.5 block w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all duration-200 outline-none";
const selectCls = inputCls;

// ─────────────────────────────────────────────────────────────────────────────
const AddLogisticsPersonnel = ({ onClose, refreshData, PersonnelToEdit }) => {
  const currentUserID = useSelector(selectUserID);

  const [activeTab, setActiveTab] = useState("identity");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingId, setIsFetchingId] = useState(false);

  const [licensePreview, setLicensePreview] = useState(licensePlaceholder);

  const [formData, setFormData] = useState({
    employeeId: "",
    firstName: "",
    lastName: "",
    role: "",
    status: "Active",
    contactNumber: "",
    email: "",
    address: "",
    emergencyContactName: "",
    emergencyContactNumber: "",
    emergencyContactRelationship: "",
    licenseNumber: "",
    licenseType: "",
    licenseRestrictionCodes: [],
    licenseExpiryDate: "",
    licenseImage: null,
  });

  // ── helpers ───────────────────────────────────────────────────────────────
  const fmtDate = (d) => {
    if (!d) return "";
    try {
      return new Date(d).toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  // ── fetch next EMP-ID from backend ───────────────────────────────────────
  const fetchNextId = async () => {
    setIsFetchingId(true);
    try {
      const { data } = await axios.get(
        `${domain}/api/LogisticsPersonnel/next-id`,
      );
      setFormData((prev) => ({ ...prev, employeeId: data.nextId }));
    } catch {
      toast.error("Failed to fetch next Employee ID");
    } finally {
      setIsFetchingId(false);
    }
  };

  // ── populate on edit / fetch ID on add ───────────────────────────────────
  useEffect(() => {
    if (PersonnelToEdit) {
      setFormData({
        employeeId: PersonnelToEdit.employeeId || "",
        firstName: PersonnelToEdit.firstName || "",
        lastName: PersonnelToEdit.lastName || "",
        role: PersonnelToEdit.role || "",
        status: PersonnelToEdit.status || "Active",
        contactNumber: PersonnelToEdit.contactNumber || "",
        email: PersonnelToEdit.email || "",
        address: PersonnelToEdit.address || "",
        emergencyContactName: PersonnelToEdit.emergencyContactName || "",
        emergencyContactNumber: PersonnelToEdit.emergencyContactNumber || "",
        emergencyContactRelationship:
          PersonnelToEdit.emergencyContactRelationship || "",
        licenseNumber: PersonnelToEdit.licenseNumber || "",
        licenseType: PersonnelToEdit.licenseType || "",
        licenseRestrictionCodes: PersonnelToEdit.licenseRestrictionCodes
          ? PersonnelToEdit.licenseRestrictionCodes
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : [],
        licenseExpiryDate: fmtDate(PersonnelToEdit.licenseExpiryDate),
        licenseImage: null,
      });
      if (PersonnelToEdit.licenseImage) {
        setLicensePreview(
          `data:image/jpeg;base64,${PersonnelToEdit.licenseImage}`,
        );
      } else {
        setLicensePreview(licensePlaceholder);
      }
    } else {
      fetchNextId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PersonnelToEdit]);

  // cleanup blob URLs
  useEffect(() => {
    return () => {
      if (licensePreview && licensePreview.startsWith("blob:"))
        URL.revokeObjectURL(licensePreview);
    };
  }, [licensePreview]);

  // ── generic input handler ─────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleRestrictionToggle = (code) => {
    setFormData((prev) => ({
      ...prev,
      licenseRestrictionCodes: prev.licenseRestrictionCodes.includes(code)
        ? prev.licenseRestrictionCodes.filter((c) => c !== code)
        : [...prev.licenseRestrictionCodes, code],
    }));
  };

  const handleLicenseImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLicensePreview(previewUrl);
      setFormData((prev) => ({ ...prev, licenseImage: file }));
    }
  };

  // ── submit ────────────────────────────────────────────────────────────────
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const apiUrl = `${domain}/api/LogisticsPersonnel`;

    let licenseImageBase64 = null;
    if (formData.licenseImage) {
      const base64String = await toBase64(formData.licenseImage);
      licenseImageBase64 = base64String ? base64String.split(",")[1] : null;
    }

    const payload = {
      EmployeeId: formData.employeeId,
      FirstName: formData.firstName,
      LastName: formData.lastName,
      Role: formData.role,
      Status: formData.status,
      ContactNumber: formData.contactNumber || null,
      Email: formData.email || null,
      Address: formData.address || null,
      EmergencyContactName: formData.emergencyContactName || null,
      EmergencyContactNumber: formData.emergencyContactNumber || null,
      EmergencyContactRelationship:
        formData.emergencyContactRelationship || null,
      LicenseNumber: formData.licenseNumber || null,
      LicenseType: formData.licenseType || null,
      LicenseRestrictionCodes:
        formData.licenseRestrictionCodes.length > 0
          ? formData.licenseRestrictionCodes.join(", ")
          : null,
      LicenseExpiryDate: formData.licenseExpiryDate || null,
      LicenseImage: licenseImageBase64,
      UserID: currentUserID,
    };

    try {
      if (PersonnelToEdit) {
        if (!payload.LicenseImage) delete payload.LicenseImage;
        const id = PersonnelToEdit.id || PersonnelToEdit.Id;
        await axios.put(`${apiUrl}/${id}`, payload);
        toast.success("Personnel updated successfully!");
      } else {
        console.log("Final bulkData:", JSON.stringify(payload, null, 2));
        await axios.post(apiUrl, payload);
        toast.success("Personnel added successfully!");
      }
      refreshData();
      onClose();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.title ||
        "Failed to save";
      toast.error(`Error: ${msg}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // TAB: Identity & Contact
  // ─────────────────────────────────────────────────────────────────────────
  const renderIdentityTab = () => (
    <div className="space-y-8">
      {/* --- Section: Basic Info --- */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-5 flex items-center gap-2 uppercase tracking-wide">
          <div className="w-2 h-2 rounded-full bg-indigo-600"></div>
          Basic Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <label
              htmlFor="employeeId"
              className="block text-sm font-semibold text-gray-700"
            >
              Employee ID <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 items-start">
              <input
                id="employeeId"
                type="text"
                required
                placeholder="EMP-00000000001"
                className={`${inputCls} font-mono tracking-wider font-medium text-indigo-700 bg-indigo-50/40 border-indigo-100`}
                value={formData.employeeId}
                onChange={handleInputChange}
              />
              <button
                type="button"
                onClick={fetchNextId}
                disabled={isFetchingId}
                title="Get next available ID"
                className="shrink-0 px-3.5 py-2.5 mt-1.5 border border-gray-200 rounded-xl bg-white text-gray-500 hover:bg-gray-50 hover:text-indigo-600 focus:ring-4 focus:ring-indigo-500/10 transition-all shadow-sm"
              >
                <RefreshCw
                  size={18}
                  className={isFetchingId ? "animate-spin" : ""}
                />
              </button>
            </div>
          </div>

          <div className="lg:col-span-1">
            <label
              htmlFor="firstName"
              className="block text-sm font-semibold text-gray-700"
            >
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="firstName"
              type="text"
              required
              placeholder="Juan"
              className={inputCls}
              value={formData.firstName}
              onChange={handleInputChange}
            />
          </div>

          <div className="lg:col-span-1">
            <label
              htmlFor="lastName"
              className="block text-sm font-semibold text-gray-700"
            >
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              id="lastName"
              type="text"
              required
              placeholder="Dela Cruz"
              className={inputCls}
              value={formData.lastName}
              onChange={handleInputChange}
            />
          </div>

          <div className="lg:col-span-1">
            <label
              htmlFor="role"
              className="block text-sm font-semibold text-gray-700"
            >
              Role <span className="text-red-500">*</span>
            </label>
            <select
              id="role"
              required
              className={selectCls}
              value={formData.role}
              onChange={handleInputChange}
            >
              <option value="">-- Select Role --</option>
              <option value="Driver">Driver</option>
              <option value="Personnel">Personnel</option>
            </select>
          </div>

          <div className="lg:col-span-1">
            <label
              htmlFor="status"
              className="block text-sm font-semibold text-gray-700"
            >
              Status
            </label>
            <select
              id="status"
              className={selectCls}
              value={formData.status}
              onChange={handleInputChange}
            >
              <option value="Active">Active</option>
              <option value="On Leave">On Leave</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <hr className="border-gray-100" />

      {/* --- Section: Contact Info --- */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-5 flex items-center gap-2 uppercase tracking-wide">
          <div className="w-2 h-2 rounded-full bg-indigo-600"></div>
          Contact Information{" "}
          <span className="text-xs font-normal text-gray-400 normal-case ml-2">
            (Optional)
          </span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="contactNumber"
              className="block text-sm font-semibold text-gray-700"
            >
              Contact Number
            </label>
            <input
              id="contactNumber"
              type="text"
              placeholder="09XXXXXXXXX"
              className={inputCls}
              value={formData.contactNumber}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-semibold text-gray-700"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              placeholder="juan@example.com"
              className={inputCls}
              value={formData.email}
              onChange={handleInputChange}
            />
          </div>
          <div className="md:col-span-2">
            <label
              htmlFor="address"
              className="block text-sm font-semibold text-gray-700"
            >
              Home Address
            </label>
            <textarea
              id="address"
              rows={2}
              placeholder="Street, Barangay, City, Province"
              className={`${inputCls} resize-none`}
              value={formData.address}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>

      <hr className="border-gray-100" />

      {/* --- Section: Emergency Contact --- */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-5 flex items-center gap-2 uppercase tracking-wide">
          <div className="w-2 h-2 rounded-full bg-red-500"></div>
          Emergency Contact{" "}
          <span className="text-xs font-normal text-gray-400 normal-case ml-2">
            (Optional)
          </span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <label
              htmlFor="emergencyContactName"
              className="block text-sm font-semibold text-gray-700"
            >
              Contact Name
            </label>
            <input
              id="emergencyContactName"
              type="text"
              placeholder="Maria Dela Cruz"
              className={inputCls}
              value={formData.emergencyContactName}
              onChange={handleInputChange}
            />
          </div>
          <div className="md:col-span-1">
            <label
              htmlFor="emergencyContactNumber"
              className="block text-sm font-semibold text-gray-700"
            >
              Contact Number
            </label>
            <input
              id="emergencyContactNumber"
              type="text"
              placeholder="09XXXXXXXXX"
              className={inputCls}
              value={formData.emergencyContactNumber}
              onChange={handleInputChange}
            />
          </div>
          <div className="md:col-span-1">
            <label
              htmlFor="emergencyContactRelationship"
              className="block text-sm font-semibold text-gray-700"
            >
              Relationship
            </label>
            <select
              id="emergencyContactRelationship"
              className={selectCls}
              value={formData.emergencyContactRelationship}
              onChange={handleInputChange}
            >
              <option value="">-- Select --</option>
              <option value="Spouse">Spouse</option>
              <option value="Parent">Parent</option>
              <option value="Sibling">Sibling</option>
              <option value="Child">Child</option>
              <option value="Relative">Relative</option>
              <option value="Friend">Friend</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TAB: Driver License
  // ─────────────────────────────────────────────────────────────────────────
  const renderLicenseTab = () => (
    <div className="space-y-8">
      <div className="bg-amber-50/50 border border-amber-200/60 rounded-xl p-4 flex items-start gap-4">
        <div className="mt-0.5 bg-amber-100 p-2 rounded-lg text-amber-600">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div>
          <p className="text-sm font-bold text-amber-800">
            Driver License Details
          </p>
          <p className="text-xs text-amber-700/80 mt-1">
            All fields in this section are entirely optional. Please fill in
            only what applies to this personnel.
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-10">
        {/* Left: License Image Upload */}
        <div className="w-full md:w-80 flex-shrink-0 flex flex-col items-center">
          <label className="block text-sm font-semibold text-gray-700 mb-3 w-full text-center">
            Scanned License Copy
          </label>
          <div className="w-full aspect-[3/2] overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 mb-4 bg-gray-50 flex items-center justify-center relative group transition-colors hover:border-indigo-400">
            <img
              src={licensePreview}
              alt="License Preview"
              className="object-contain w-full h-full p-1"
              onError={(e) => (e.target.src = licensePlaceholder)}
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
              <span className="text-white text-sm font-medium flex items-center gap-2">
                <ImagePlus size={18} /> Click below to change
              </span>
            </div>
          </div>
          <input
            type="file"
            id="licenseImageFile"
            onChange={handleLicenseImageChange}
            accept="image/*"
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100 cursor-pointer transition-colors"
          />
        </div>

        {/* Right: Details & Restrictions */}
        <div className="flex-1 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="sm:col-span-2">
              <label
                htmlFor="licenseNumber"
                className="block text-sm font-semibold text-gray-700"
              >
                License Number
              </label>
              <input
                id="licenseNumber"
                type="text"
                placeholder="A01-23-456789"
                className={`${inputCls} font-mono uppercase tracking-wider`}
                value={formData.licenseNumber}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label
                htmlFor="licenseType"
                className="block text-sm font-semibold text-gray-700"
              >
                License Type
              </label>
              <select
                id="licenseType"
                className={selectCls}
                value={formData.licenseType}
                onChange={handleInputChange}
              >
                <option value="">-- Select Type --</option>
                <option value="Professional">Professional</option>
                <option value="Non-Professional">Non-Professional</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="licenseExpiryDate"
                className="block text-sm font-semibold text-gray-700"
              >
                Expiry Date
              </label>
              <input
                id="licenseExpiryDate"
                type="date"
                className={inputCls}
                value={formData.licenseExpiryDate}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Restriction Codes
              <span className="ml-2 text-xs font-normal text-gray-400">
                Click to toggle applicable codes
              </span>
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
              {RESTRICTION_CODES.map((code) => {
                const active = formData.licenseRestrictionCodes.includes(code);
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => handleRestrictionToggle(code)}
                    className={`aspect-square rounded-xl text-lg font-bold border-2 transition-all duration-200 ${
                      active
                        ? "bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/30 scale-105"
                        : "bg-white text-gray-500 border-gray-200 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50"
                    }`}
                  >
                    {code}
                  </button>
                );
              })}
            </div>
            {formData.licenseRestrictionCodes.length > 0 && (
              <p className="text-sm text-indigo-600 font-medium mt-4 bg-indigo-50 inline-block px-3 py-1.5 rounded-lg border border-indigo-100">
                Selected: {formData.licenseRestrictionCodes.sort().join(", ")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  const TABS = [
    { key: "identity", label: "Identity & Contact Details" },
    { key: "license", label: "Driver License Info" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[92vh] overflow-hidden">
        {isLoading && <Loader />}

        {/* Modern Header */}
        <div className="px-8 py-6 bg-white flex justify-between items-center z-10 border-b border-gray-100">
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              {PersonnelToEdit
                ? "Update Personnel Record"
                : "Add Logistics Personnel"}
            </h2>
            <p className="text-sm text-gray-500 mt-1.5">
              Manage driver profiles, contact details, and licensing records
              efficiently.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Styled Tab Bar */}
        <div className="px-8 bg-gray-50/50 border-b border-gray-200 z-10">
          <nav className="flex space-x-10">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-bold text-sm transition-all duration-200 ${
                  activeTab === tab.key
                    ? "border-indigo-600 text-indigo-700"
                    : "border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Scrollable Form Body */}
        <form
          onSubmit={handleFormSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
            {activeTab === "identity"
              ? renderIdentityTab()
              : renderLicenseTab()}
          </div>

          {/* Elegant Footer */}
          <div className="px-8 py-5 bg-white border-t border-gray-100 flex justify-end gap-4 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)]">
            {activeTab === "identity" ? (
              <button
                type="button"
                onClick={() => setActiveTab("license")}
                className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/30 hover:shadow-indigo-600/40 focus:ring-4 focus:ring-indigo-600/20 active:scale-95"
              >
                Next Step ➔
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setActiveTab("identity")}
                  className="px-8 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-50 hover:text-gray-900 transition-all focus:ring-4 focus:ring-gray-200/50 active:scale-95"
                >
                  Go Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/30 hover:shadow-indigo-600/40 focus:ring-4 focus:ring-indigo-600/20 disabled:opacity-70 disabled:cursor-not-allowed active:scale-95"
                >
                  {isLoading
                    ? "Processing..."
                    : PersonnelToEdit
                      ? "Update Record"
                      : "Finish & Save"}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLogisticsPersonnel;
